/**
 * The clinic cash drawer, as the feed reports it and as the books hold it.
 *
 * Three things on this screen, in the order a person needs them: whether
 * the feed is running and what blocks it; each clinic-day with its four
 * verdicts; and the rows behind a day. The one lever is "Pull now".
 *
 * It is cash only -- about one rupee in eight of what the clinics collect --
 * and it never records revenue. That sentence is on the screen because
 * somebody will otherwise read a till balance as a day's takings.
 *
 * The fourth thing is each deposit against the bank statement: matched to
 * the credit that was it, offered credits a person has to choose between,
 * or still unmatched -- which is cash between a clinic and a branch.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note, Pill, Stat } from '../components/ui'

interface Run {
  id: string
  started_at: string
  finished_at: string | null
  window_from: string
  window_to: string
  clamped: boolean
  start_from: string | null
  trigger: string
  dry_run: boolean
  rows_seen: number
  rows_new: number
  rows_changed: number
  rows_voided: number
  posted: number
  reversed: number
  deposits_matched: number
  complete_clinics: string[]
  outcome: string
  error: string | null
}

interface Day {
  id: string
  clinic_code: string
  clinic_name: string | null
  business_date: string
  feed_opening_paise: number
  feed_cash_in_paise: number
  feed_refunds_paise: number
  feed_deposited_paise: number
  feed_spent_paise: number
  feed_handed_over_paise: number
  feed_handed_in_paise: number
  feed_closing_paise: number
  feed_counted_paise: number | null
  feed_variance_paise: number | null
  rows_cash_in_paise: number
  rows_refunds_paise: number
  rows_deposited_paise: number
  rows_spent_paise: number
  rows_handed_over_paise: number
  rows_handed_in_paise: number
  ledger_movement_paise: number | null
  identity_ok: boolean | null
  rows_tie: boolean | null
  chain_ok: boolean | null
  ledger_ties: boolean | null
  ties: boolean
  problems: string[]
  checked_at: string | null
  staged: number
  posted: number
  held: number
}

interface Row {
  id: string
  feed_id: string
  row_type: string
  direction: string
  business_date: string
  recorded_at: string | null
  entered_late: boolean
  clinic_code: string
  amount_paise: number
  voided: boolean
  void_reason: string | null
  label: string | null
  reference: string | null
  voucher_no: string | null
  party: string | null
  note: string | null
  feed_category: string | null
  count_counted_paise: number | null
  count_computed_paise: number | null
  count_variance_paise: number | null
  count_applied: boolean | null
  state: string
  problem: string | null
  journal_entry_id: string | null
  reversal_entry_id: string | null
  consecutive_absences: number
  bank_match: BankMatch | null
}

interface BankMatch {
  id: string
  state: string
  method: string
  bank_transaction_id: string
  bank_date: string
  bank_amount_paise: number
  bank_narration: string
  bank_account_name: string | null
  bank_journal_entry_id: string | null
  difference_paise: number
  evidence: string[]
  problem: string | null
  matched_at: string | null
  released_at: string | null
  release_reason: string | null
}

interface Deposit {
  id: string
  feed_id: string
  clinic_code: string
  clinic_name: string | null
  business_date: string
  amount_paise: number
  reference: string | null
  party: string | null
  state: string
  voided: boolean
  bank_status: string
  days_open: number
  overdue: boolean
  match: BankMatch | null
  candidates: BankMatch[]
}

interface Summary {
  enabled: boolean
  base_url: string
  last_run: Run | null
  rows_by_state: Record<string, number>
  clinics_without_a_till: string[]
  days_not_tying: number
  held_rows: number
  deposits_open: number
  deposits_overdue: number
  credits_without_a_deposit: number
}

const BANK_STATUS_CLASS: Record<string, string> = {
  matched: 'approved',
  candidates: 'advisory',
  unmatched: 'returned',
  'not posted': 'draft',
}

const METHOD_LABEL: Record<string, string> = {
  reference: 'by slip reference',
  amount_and_date: 'by amount and date',
  manual: 'by hand',
}

const TYPE_LABEL: Record<string, string> = {
  cash_in: 'cash in',
  refund: 'refund',
  bank_deposit: 'to the bank',
  expense: 'spent from the drawer',
  handover_out: 'handed to another branch',
  handover_in: 'received from another branch',
  count: 'counted',
}

function Verdict({ ok, label }: { ok: boolean | null; label: string }) {
  return (
    <span className={`pill ${ok === null ? 'advisory' : ok ? 'approved' : 'returned'}`}>
      {label}
    </span>
  )
}

interface CashDay {
  business_date: string
  opening_paise: number
  taken_paise: number
  refunded_paise: number
  banked_paise: number
  spent_paise: number
  handed_over_paise: number
  handed_in_paise: number
  adjustments_paise: number
  closing_paise: number
  drawer_closing_paise: number | null
  drawer_counted_paise: number | null
  vs_drawer_paise: number | null
  vs_count_paise: number | null
}

interface CashEntry {
  id: string
  business_date: string
  amount_paise: number
  reason: string | null
  entered_by_name: string | null
  entered_at: string
}

interface CashBook {
  clinic_code: string
  clinic_name: string
  start: string
  clinics: { code: string; name: string }[]
  opening_set: boolean
  opening: CashEntry | null
  drawer_first_opening_paise: number | null
  opening_paise: number
  opening_source: string
  days: CashDay[]
  adjustments: CashEntry[]
}

//: Rupees as typed -- "1,662", "1662.50", "-25" -- to paise. null if it is not a number.
function toPaise(text: string): number | null {
  const clean = text.replace(/[,₹\s]/g, '')
  if (!/^-?\d+(\.\d{1,2})?$/.test(clean)) return null
  const negative = clean.startsWith('-')
  const [whole, frac = ''] = clean.replace('-', '').split('.')
  const paise = Number(whole) * 100 + Number((frac + '00').slice(0, 2))
  return negative ? -paise : paise
}

function Gap({ paise }: { paise: number | null }) {
  if (paise === null) return <span className="faint">—</span>
  if (paise === 0) return <span className="pill approved">agrees</span>
  return (
    <span className="pill returned">
      {paise > 0 ? '+' : '−'}
      {formatInr(Math.abs(paise))}
    </span>
  )
}

function CashBookCard() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [openingText, setOpeningText] = useState('')
  const [openingReason, setOpeningReason] = useState('')
  const [adjDate, setAdjDate] = useState('')
  const [adjText, setAdjText] = useState('')
  const [adjReason, setAdjReason] = useState('')
  const book = useQuery({
    queryKey: ['clinic-drawer', 'cash-book', code],
    queryFn: () => api.get<CashBook>(`/clinic-drawer/cash-book${code ? `?clinic=${code}` : ''}`),
  })
  const fresh = (data: CashBook) => {
    queryClient.setQueryData(['clinic-drawer', 'cash-book', code], data)
    void queryClient.invalidateQueries({ queryKey: ['clinic-drawer', 'cash-book'] })
  }
  const setOpening = useMutation({
    mutationFn: () =>
      api.put<CashBook>(`/clinic-drawer/cash-book/${b!.clinic_code}/opening`, {
        amount_paise: toPaise(openingText),
        reason: openingReason || null,
      }),
    onSuccess: (data) => {
      setOpeningText('')
      setOpeningReason('')
      fresh(data)
    },
  })
  const clearOpening = useMutation({
    mutationFn: (reason: string) =>
      api.post<CashBook>(`/clinic-drawer/cash-book/${b!.clinic_code}/opening/clear`, { reason }),
    onSuccess: fresh,
  })
  const addAdjustment = useMutation({
    mutationFn: () =>
      api.post<CashBook>(`/clinic-drawer/cash-book/${b!.clinic_code}/adjustments`, {
        business_date: adjDate,
        amount_paise: toPaise(adjText),
        reason: adjReason,
      }),
    onSuccess: (data) => {
      setAdjText('')
      setAdjReason('')
      fresh(data)
    },
  })
  const withdraw = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.post<CashBook>(`/clinic-drawer/cash-book/adjustments/${args.id}/withdraw`, {
        reason: args.reason,
      }),
    onSuccess: fresh,
  })

  const b = book.data
  const canEdit = can('clinic.drawer.post')
  const last = b?.days[b.days.length - 1]
  return (
    <Card
      title="Cash book"
      hint="each clinic’s cash from the first day, carried forward: opening, cash the bills say was taken, what the drawer says left"
      actions={
        <select
          value={b?.clinic_code ?? code}
          onChange={(e) => setCode(e.target.value)}
          style={{ width: 'auto' }}
        >
          {(b?.clinics ?? []).map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      }
      tight
      wide
    >
      <ErrorNote error={book.error ?? setOpening.error ?? clearOpening.error ?? addAdjustment.error ?? withdraw.error} />
      {book.isLoading || !b ? (
        <Loading what="Building the cash book" />
      ) : (
        <>
          <div className="grid">
            <Stat
              label={`Opening on ${b.start}`}
              value={b.opening_source === 'none' ? 'no drawer figure' : formatInr(b.opening_paise)}
              note={
                b.opening_source === 'entered'
                  ? `typed by ${b.opening?.entered_by_name ?? 'someone'}, in place of the drawer’s ${
                      b.drawer_first_opening_paise !== null
                        ? formatInr(b.drawer_first_opening_paise)
                        : 'figure'
                    }`
                  : b.opening_source === 'drawer'
                    ? 'from the clinic’s cash drawer; earlier cash is corrected there or as an adjustment'
                    : 'the clinic’s drawer has no figure for that morning; type one to start'
              }
            />
            <Stat
              label="Cash in hand now, by this book"
              value={last ? formatInr(last.closing_paise) : '—'}
              note={
                last?.drawer_closing_paise != null
                  ? `the clinic’s drawer says ${formatInr(last.drawer_closing_paise)}`
                  : 'no drawer figure for today yet'
              }
            />
          </div>

          {canEdit && (
            <form
              className="row"
              style={{ marginBottom: 10 }}
              onSubmit={(e) => {
                e.preventDefault()
                if (toPaise(openingText) !== null) setOpening.mutate()
              }}
            >
              <input
                placeholder={`Opening on ${b.start} (₹)`}
                value={openingText}
                onChange={(e) => setOpeningText(e.target.value)}
                style={{ width: 200 }}
              />
              <input
                placeholder={b.opening_set ? 'Why it is changing' : 'Why it differs from the drawer'}
                value={openingReason}
                onChange={(e) => setOpeningReason(e.target.value)}
                style={{ flex: 1, minWidth: 180 }}
              />
              <div className="shrink">
                <button
                  className="primary"
                  type="submit"
                  disabled={setOpening.isPending || toPaise(openingText) === null}
                >
                  {b.opening_set ? 'Change the typed opening' : 'Override the drawer'}
                </button>
              </div>
              {b.opening_set && (
                <div className="shrink">
                  <button
                    type="button"
                    disabled={clearOpening.isPending}
                    onClick={() => {
                      const reason = window.prompt(
                        'Go back to the clinic’s drawer for the opening? Say why.',
                      )
                      if (reason && reason.trim().length >= 3) clearOpening.mutate(reason.trim())
                    }}
                  >
                    Use the drawer’s
                  </button>
                </div>
              )}
            </form>
          )}

          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th className="num">Opening</th>
                <th className="num">Cash taken</th>
                <th className="num">Refunded</th>
                <th className="num">Banked</th>
                <th className="num">Spent</th>
                <th className="num">Handed on</th>
                <th className="num">Adjusted</th>
                <th className="num">Closing</th>
                <th className="num">Drawer says</th>
                <th>vs drawer</th>
                <th>vs count</th>
              </tr>
            </thead>
            <tbody>
              {b.days.map((d) => (
                <tr key={d.business_date}>
                  <td className="date">{d.business_date}</td>
                  <td className="num">{formatInr(d.opening_paise)}</td>
                  <td className="num">{formatInr(d.taken_paise)}</td>
                  <td className="num">{formatInr(d.refunded_paise)}</td>
                  <td className="num">{formatInr(d.banked_paise)}</td>
                  <td className="num">{formatInr(d.spent_paise)}</td>
                  <td className="num">{formatInr(d.handed_over_paise - d.handed_in_paise)}</td>
                  <td className="num">
                    {d.adjustments_paise ? formatInr(d.adjustments_paise) : '—'}
                  </td>
                  <td className="num">
                    <strong>{formatInr(d.closing_paise)}</strong>
                  </td>
                  <td className="num">
                    {d.drawer_closing_paise === null ? '—' : formatInr(d.drawer_closing_paise)}
                    {d.drawer_counted_paise !== null && (
                      <div className="faint">counted {formatInr(d.drawer_counted_paise)}</div>
                    )}
                  </td>
                  <td>
                    <Gap paise={d.vs_drawer_paise} />
                  </td>
                  <td>
                    <Gap paise={d.vs_count_paise} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ marginTop: 12 }}>
            <div className="muted" style={{ marginBottom: 6 }}>
              Adjustments — cash found, a shortage written down, a receipt the clinic software
              missed. Each moves its day and every day after it.
            </div>
            {b.adjustments.length > 0 && (
              <table>
                <tbody>
                  {b.adjustments.map((a) => (
                    <tr key={a.id}>
                      <td className="date">{a.business_date}</td>
                      <td className="num">{formatInr(a.amount_paise)}</td>
                      <td className="muted">{a.reason}</td>
                      <td className="faint">{a.entered_by_name}</td>
                      <td className="num">
                        {canEdit && (
                          <button
                            className="small"
                            disabled={withdraw.isPending}
                            onClick={() => {
                              const reason = window.prompt('Why is this adjustment being taken out?')
                              if (reason && reason.trim().length >= 3)
                                withdraw.mutate({ id: a.id, reason: reason.trim() })
                            }}
                          >
                            Take out
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {canEdit && (
              <form
                className="row"
                style={{ marginTop: 6 }}
                onSubmit={(e) => {
                  e.preventDefault()
                  if (adjDate && toPaise(adjText) && adjReason.trim().length >= 3)
                    addAdjustment.mutate()
                }}
              >
                <input
                  type="date"
                  min={b.start}
                  value={adjDate}
                  onChange={(e) => setAdjDate(e.target.value)}
                  style={{ width: 160 }}
                />
                <input
                  placeholder="₹ (+ found, − short)"
                  value={adjText}
                  onChange={(e) => setAdjText(e.target.value)}
                  style={{ width: 170 }}
                />
                <input
                  placeholder="Reason"
                  value={adjReason}
                  onChange={(e) => setAdjReason(e.target.value)}
                  style={{ flex: 1, minWidth: 180 }}
                />
                <div className="shrink">
                  <button
                    type="submit"
                    disabled={
                      addAdjustment.isPending ||
                      !adjDate ||
                      !toPaise(adjText) ||
                      adjReason.trim().length < 3
                    }
                  >
                    Add adjustment
                  </button>
                </div>
              </form>
            )}
          </div>
        </>
      )}
    </Card>
  )
}

export default function ClinicDrawer() {
  const { can } = useAuth()
  const queryClient = useQueryClient()
  const [clinic, setClinic] = useState('')
  const [open, setOpen] = useState<Day | null>(null)
  const [onlyOpenDeposits, setOnlyOpenDeposits] = useState(true)
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['clinic-drawer'] })

  const summary = useQuery({
    queryKey: ['clinic-drawer', 'summary'],
    queryFn: () => api.get<Summary>('/clinic-drawer/summary'),
  })
  const days = useQuery({
    queryKey: ['clinic-drawer', 'days', clinic],
    queryFn: () =>
      api.get<Day[]>(`/clinic-drawer/days?days_back=35${clinic ? `&clinic=${clinic}` : ''}`),
  })
  const rows = useQuery({
    queryKey: ['clinic-drawer', 'rows', open?.clinic_code, open?.business_date],
    queryFn: () =>
      api.get<Row[]>(`/clinic-drawer/rows?clinic=${open!.clinic_code}&on=${open!.business_date}`),
    enabled: !!open,
  })
  const deposits = useQuery({
    queryKey: ['clinic-drawer', 'deposits', clinic, onlyOpenDeposits],
    queryFn: () =>
      api.get<Deposit[]>(
        `/clinic-drawer/deposits?only_open=${onlyOpenDeposits}${clinic ? `&clinic=${clinic}` : ''}`,
      ),
  })
  const runs = useQuery({
    queryKey: ['clinic-drawer', 'runs'],
    queryFn: () => api.get<Run[]>('/clinic-drawer/runs'),
  })

  const pull = useMutation({
    mutationFn: (dry_run: boolean) =>
      api.post<Run>('/clinic-drawer/sync', { dry_run, full_sweep: false }),
    onSuccess: refresh,
  })
  const repost = useMutation({
    mutationFn: (id: string) => api.post<Row>(`/clinic-drawer/rows/${id}/repost`),
    onSuccess: refresh,
  })
  const matchDeposit = useMutation({
    mutationFn: (args: { id: string; bank_transaction_id: string; reason: string }) =>
      api.post<Deposit>(`/clinic-drawer/deposits/${args.id}/match`, {
        bank_transaction_id: args.bank_transaction_id,
        reason: args.reason,
      }),
    onSuccess: refresh,
  })
  const releaseDeposit = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      api.post<Deposit>(`/clinic-drawer/deposits/${args.id}/release`, { reason: args.reason }),
    onSuccess: refresh,
  })

  if (summary.isLoading) return <Loading what="Loading the clinic drawer" />
  const s = summary.data
  const last = s?.last_run ?? null
  const clinics = Array.from(new Set((days.data ?? []).map((d) => d.clinic_code))).sort()

  return (
    <>
      <ErrorNote
        error={
          summary.error ??
          days.error ??
          pull.error ??
          repost.error ??
          deposits.error ??
          matchDeposit.error ??
          releaseDeposit.error
        }
      />

      <Note>
        This is the clinics’ <strong>cash</strong> drawer, as the clinic software reports it:
        roughly one rupee in eight of what the clinics collect. Card, UPI and cheque never pass
        through it. Nothing here records revenue — the day’s takings sheet still does that — so a
        till balance is cash on hand, not a day’s income.
      </Note>

      {s && !s.enabled && (
        <Note kind="warn">
          The feed is not switched on for this server. It needs the clinic software’s key
          (CLINIC_FEED_KEY) and CLINIC_FEED_ENABLED on both the api and the scheduler.
        </Note>
      )}
      {s && s.clinics_without_a_till.length > 0 && (
        <Note kind="warn">
          No approved cash till in the books for: {s.clinics_without_a_till.join(', ')}. Rows for
          those clinics are held until a till exists — add one under Bank statements (kind: cash, at
          that clinic) and have it approved.
        </Note>
      )}

      <div className="grid">
        <Stat
          label="Last pull"
          value={last ? last.outcome.replace(/_/g, ' ') : 'never'}
          note={
            last
              ? `${last.started_at.slice(0, 16).replace('T', ' ')} · ${last.rows_seen} rows seen, ${last.posted} posted, ${last.reversed} reversed`
              : 'nothing has been pulled yet'
          }
        />
        <Stat
          label="Days not tying"
          value={String(s?.days_not_tying ?? 0)}
          note="in the last 35 days; nothing on those days posts"
        />
        <Stat
          label="Rows held"
          value={String(s?.held_rows ?? 0)}
          note="could not post; each one says why"
        />
        <Stat
          label="Rows posted"
          value={String(s?.rows_by_state.posted ?? 0)}
          note={`${s?.rows_by_state.staged ?? 0} staged · ${s?.rows_by_state.reversed ?? 0} reversed`}
        />
        <Stat
          label="Deposits not yet at the bank"
          value={String(s?.deposits_open ?? 0)}
          note={
            (s?.deposits_overdue ?? 0) > 0
              ? `${s?.deposits_overdue} older than the allowance`
              : (s?.credits_without_a_deposit ?? 0) > 0
                ? `${s?.credits_without_a_deposit} bank credit(s) with no deposit behind them`
                : 'cash that left a drawer and has not been seen on a statement'
          }
        />
      </div>

      {can('clinic.drawer.post') && (
        <div className="row" style={{ marginBottom: 12 }}>
          <div className="shrink">
            <button
              className="primary"
              disabled={pull.isPending || !s?.enabled}
              onClick={() => pull.mutate(false)}
            >
              {pull.isPending ? 'Pulling…' : 'Pull the feed now'}
            </button>
          </div>
          <div className="shrink">
            <button disabled={pull.isPending || !s?.enabled} onClick={() => pull.mutate(true)}>
              Dry run
            </button>
          </div>
          {last?.error && (
            <div className="shrink muted" style={{ fontSize: 13 }}>
              last run: {last.error}
            </div>
          )}
        </div>
      )}

      <CashBookCard />

      <Card
        title="Clinic-days"
        hint="each day checked four ways; a day that fails any of the first three does not post"
        actions={
          <select value={clinic} onChange={(e) => setClinic(e.target.value)} style={{ width: 'auto' }}>
            <option value="">Every clinic</option>
            {clinics.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        }
        tight
        wide
      >
        {(days.data ?? []).length === 0 ? (
          <div className="empty">No days from the feed yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Day</th>
                <th>Clinic</th>
                <th className="num">Opening</th>
                <th className="num">Cash in</th>
                <th className="num">Out</th>
                <th className="num">Closing</th>
                <th className="num">Counted</th>
                <th>Checks</th>
                <th>Rows</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(days.data ?? []).map((d) => (
                <tr key={d.id}>
                  <td className="date">{d.business_date}</td>
                  <td>{d.clinic_name ?? d.clinic_code}</td>
                  <td className="num">{formatInr(d.feed_opening_paise)}</td>
                  <td className="num">{formatInr(d.feed_cash_in_paise)}</td>
                  <td className="num">
                    {formatInr(
                      d.feed_refunds_paise +
                        d.feed_deposited_paise +
                        d.feed_spent_paise +
                        d.feed_handed_over_paise -
                        d.feed_handed_in_paise,
                    )}
                  </td>
                  <td className="num">{formatInr(d.feed_closing_paise)}</td>
                  <td className="num">
                    {d.feed_counted_paise === null ? (
                      <span className="faint">—</span>
                    ) : (
                      <>
                        {formatInr(d.feed_counted_paise)}
                        {d.feed_variance_paise ? (
                          <div className="faint">
                            {d.feed_variance_paise > 0 ? 'over' : 'short'} by{' '}
                            {formatInr(Math.abs(d.feed_variance_paise))}
                          </div>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td>
                    <Verdict ok={d.identity_ok} label="adds up" />{' '}
                    <Verdict ok={d.rows_tie} label="rows" />{' '}
                    <Verdict ok={d.chain_ok} label="chain" />{' '}
                    <Verdict ok={d.ledger_ties} label="ledger" />
                    {d.problems.length > 0 && (
                      <div className="faint">{d.problems.join('; ')}</div>
                    )}
                  </td>
                  <td className="muted">
                    {d.posted} posted
                    {d.held > 0 && <strong> · {d.held} held</strong>}
                    {d.staged > 0 && <> · {d.staged} waiting</>}
                  </td>
                  <td className="num">
                    <button
                      className="small"
                      onClick={() => setOpen(open?.id === d.id ? null : d)}
                    >
                      {open?.id === d.id ? 'Close' : 'Rows'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {open && (
        <Card
          title={`${open.clinic_name ?? open.clinic_code} — ${open.business_date}`}
          hint="every movement the feed reported for the day"
          tight
          wide
        >
          <ErrorNote error={rows.error} />
          <table>
            <thead>
              <tr>
                <th>What</th>
                <th>Who / reference</th>
                <th className="num">Amount</th>
                <th>Recorded</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(rows.data ?? []).map((r) => (
                <tr key={r.id} style={r.voided ? { opacity: 0.55 } : undefined}>
                  <td>
                    {TYPE_LABEL[r.row_type] ?? r.row_type}
                    {r.feed_category && <span className="faint"> · {r.feed_category}</span>}
                    {r.label && <div className="faint">{r.label}</div>}
                    {r.voided && (
                      <div className="faint">voided{r.void_reason ? `: ${r.void_reason}` : ''}</div>
                    )}
                  </td>
                  <td className="muted">
                    {r.party ?? ''}
                    {(r.reference || r.voucher_no) && (
                      <div className="faint mono">{[r.reference, r.voucher_no].filter(Boolean).join(' · ')}</div>
                    )}
                  </td>
                  <td className="num">
                    {r.row_type === 'count' ? (
                      <>
                        {formatInr(r.count_counted_paise ?? 0)}
                        <div className="faint">
                          counted; variance {formatInr(r.count_variance_paise ?? 0)}
                        </div>
                      </>
                    ) : (
                      <>
                        {r.direction === 'out' ? '−' : ''}
                        {formatInr(r.amount_paise)}
                      </>
                    )}
                  </td>
                  <td className="muted date">
                    {r.recorded_at ? r.recorded_at.slice(0, 16).replace('T', ' ') : '—'}
                    {r.entered_late && <div className="faint">entered late</div>}
                  </td>
                  <td>
                    <Pill value={r.state} />
                    {r.problem && <div className="faint">{r.problem}</div>}
                    {r.row_type === 'bank_deposit' && r.state === 'posted' && (
                      <div className="faint">
                        {r.bank_match
                          ? `banked ${r.bank_match.bank_date}, ${METHOD_LABEL[r.bank_match.method] ?? r.bank_match.method}`
                          : 'not yet seen at the bank'}
                      </div>
                    )}
                  </td>
                  <td className="num">
                    {r.state === 'held' && can('clinic.drawer.post') && (
                      <button
                        className="small"
                        disabled={repost.isPending}
                        onClick={() => repost.mutate(r.id)}
                      >
                        Try again
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card
        title="Deposits against the bank"
        hint="each deposit the drawer says went to the bank, and the credit on the statement that was it"
        actions={
          <label className="row" style={{ gap: 6, alignItems: 'center', fontWeight: 400 }}>
            <input
              type="checkbox"
              checked={onlyOpenDeposits}
              onChange={(e) => setOnlyOpenDeposits(e.target.checked)}
            />
            only the ones still open
          </label>
        }
        tight
        wide
      >
        <Note>
          A matched credit posts against the deposit’s clinic and clears “cash collected, not yet
          banked” for exactly that deposit. A deposit is matched by its slip reference on the bank
          line, or by the same amount a few days later on a line that reads like a cash deposit.
          Anything less waits here for a person: two credits that could be it, an amount that
          differs, a credit tagged to another clinic.
        </Note>
        {deposits.isLoading ? (
          <Loading what="Checking each deposit against the bank" />
        ) : (deposits.data ?? []).length === 0 ? (
          <div className="empty">
            {onlyOpenDeposits ? 'Every deposit is matched to a credit.' : 'No deposits from the feed yet.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Drawer</th>
                <th>Clinic</th>
                <th className="num">Amount</th>
                <th>Slip</th>
                <th>At the bank</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(deposits.data ?? []).map((d) => (
                <tr key={d.id} style={d.voided ? { opacity: 0.55 } : undefined}>
                  <td className="date">
                    {d.business_date}
                    {d.overdue && <div className="faint">{d.days_open} days open</div>}
                  </td>
                  <td>{d.clinic_name ?? d.clinic_code}</td>
                  <td className="num">{formatInr(d.amount_paise)}</td>
                  <td className="muted mono">{d.reference ?? '—'}</td>
                  <td className="muted">
                    {d.match ? (
                      <>
                        <span className="date">{d.match.bank_date}</span>
                        {d.match.bank_account_name && <> · {d.match.bank_account_name}</>}
                        <div className="faint">
                          {METHOD_LABEL[d.match.method] ?? d.match.method} · {d.match.bank_narration}
                        </div>
                      </>
                    ) : d.candidates.length > 0 ? (
                      <div className="faint">{d.candidates.length} possible credit(s), below</div>
                    ) : d.bank_status === 'not posted' ? (
                      <span className="faint">the deposit itself is not posted yet</span>
                    ) : (
                      <span className="faint">no credit on any statement yet</span>
                    )}
                  </td>
                  <td>
                    <span className={`pill ${BANK_STATUS_CLASS[d.bank_status] ?? 'draft'}`}>
                      {d.bank_status}
                    </span>
                    {d.candidates.map((c) => (
                      <div key={c.id} className="faint" style={{ marginTop: 4 }}>
                        <span className="date">{c.bank_date}</span> {formatInr(c.bank_amount_paise)}{' '}
                        <span className="mono">{c.bank_narration}</span>
                        {c.problem && <div>{c.problem}</div>}
                        {can('clinic.drawer.post') && c.difference_paise === 0 && (
                          <button
                            className="small"
                            style={{ marginTop: 2 }}
                            disabled={matchDeposit.isPending}
                            onClick={() => {
                              const reason = window.prompt(
                                `Match this ${formatInr(d.amount_paise)} deposit to the credit of ${c.bank_date}? Say why.`,
                              )
                              if (reason && reason.trim().length >= 3)
                                matchDeposit.mutate({
                                  id: d.id,
                                  bank_transaction_id: c.bank_transaction_id,
                                  reason: reason.trim(),
                                })
                            }}
                          >
                            This one
                          </button>
                        )}
                      </div>
                    ))}
                  </td>
                  <td className="num">
                    {d.match && can('clinic.drawer.post') && (
                      <button
                        className="small"
                        disabled={releaseDeposit.isPending}
                        onClick={() => {
                          const reason = window.prompt(
                            'Take this match apart? The credit’s entry is reversed. Say why.',
                          )
                          if (reason && reason.trim().length >= 3)
                            releaseDeposit.mutate({ id: d.id, reason: reason.trim() })
                        }}
                      >
                        Release
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Pulls" hint="one row per run; a partial run judged nothing vanished" tight>
        {(runs.data ?? []).length === 0 ? (
          <div className="empty">No runs yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Started</th>
                <th>Window</th>
                <th>How</th>
                <th className="num">Seen</th>
                <th className="num">New</th>
                <th className="num">Changed</th>
                <th className="num">Posted</th>
                <th className="num">Reversed</th>
                <th className="num">Banked</th>
                <th>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {(runs.data ?? []).map((r) => (
                <tr key={r.id}>
                  <td className="date">{r.started_at.slice(0, 16).replace('T', ' ')}</td>
                  <td className="muted date">
                    {r.window_from} → {r.window_to}
                    {r.clamped && <span className="faint"> (clamped)</span>}
                  </td>
                  <td className="muted">
                    {r.trigger}
                    {r.dry_run && ' · dry run'}
                  </td>
                  <td className="num">{r.rows_seen}</td>
                  <td className="num">{r.rows_new}</td>
                  <td className="num">{r.rows_changed}</td>
                  <td className="num">{r.posted}</td>
                  <td className="num">{r.reversed}</td>
                  <td className="num">{r.deposits_matched}</td>
                  <td>
                    <Pill value={r.outcome} />
                    {r.error && <div className="faint">{r.error}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  )
}
